import { theme } from 'antd';
import components from './components';
import {
  alpha,
  motion,
  palette,
  radius,
  semantic,
  shadow,
  size,
  typography,
} from './foundations';

const { brand } = palette;

const token = {
  /* Brand — explicit ramp so hover/pressed/tints stay on-palette */
  colorPrimary: brand[500],
  colorPrimaryHover: brand[400],
  colorPrimaryActive: brand[600],
  colorPrimaryBg: brand[50],
  colorPrimaryBgHover: brand[100],
  colorPrimaryBorder: brand[200],
  colorPrimaryBorderHover: brand[300],
  colorPrimaryText: brand[500],
  colorPrimaryTextHover: brand[400],
  colorPrimaryTextActive: brand[600],

  /* Status */
  colorSuccess: palette.success,
  colorWarning: palette.warning,
  colorError: palette.error,
  colorInfo: palette.info,
  colorErrorOutline: alpha(palette.error, 0.16),
  colorWarningOutline: alpha(palette.warning, 0.16),

  /* Links */
  colorLink: semantic.text.link,
  colorLinkHover: semantic.text.linkHover,
  colorLinkActive: brand[600],

  /* Text — colorTextBase also seeds antd's derived fills, so they're navy-tinted */
  colorTextBase: semantic.text.heading,
  colorText: semantic.text.primary,
  colorTextHeading: semantic.text.heading,
  colorTextSecondary: semantic.text.secondary,
  colorTextTertiary: semantic.text.tertiary,
  colorTextDescription: semantic.text.secondary,
  colorTextPlaceholder: semantic.text.tertiary,
  colorTextDisabled: semantic.text.tertiary,
  colorTextLightSolid: semantic.text.inverse,
  colorIcon: semantic.text.secondary,
  colorIconHover: semantic.text.primary,

  /* Surfaces */
  colorBgBase: palette.neutral[0],
  colorBgLayout: semantic.surface.canvas,
  colorBgContainer: semantic.surface.container,
  colorBgElevated: semantic.surface.elevated,
  colorBgSpotlight: semantic.surface.spotlight,
  colorBgContainerDisabled: semantic.surface.subtle,
  colorFillAlter: semantic.surface.subtle,

  /* Borders */
  colorBorder: semantic.border.default,
  colorBorderSecondary: semantic.border.subtle,
  colorSplit: semantic.border.subtle,

  /* Interaction */
  controlItemBgHover: semantic.state.hover,
  controlItemBgActive: semantic.state.selected,
  controlItemBgActiveHover: semantic.state.selectedHover,
  controlOutline: semantic.state.focusRing,
  controlOutlineWidth: 3,

  /* Typography */
  fontFamily: typography.fontFamily,
  fontFamilyCode: typography.fontFamilyCode,
  fontSize: typography.size.base,
  fontSizeSM: typography.size.sm,
  fontSizeLG: typography.size.lg,
  fontSizeXL: typography.size.xl,
  fontSizeHeading1: typography.size.h1,
  fontSizeHeading2: typography.size.h2,
  fontSizeHeading3: typography.size.h3,
  fontSizeHeading4: typography.size.h4,
  fontSizeHeading5: typography.size.h5,
  lineHeight: typography.lineHeight,
  fontWeightStrong: typography.weight.semibold,

  /* Shape & size */
  borderRadiusXS: radius.xs,
  borderRadiusSM: radius.sm,
  borderRadius: radius.md,
  borderRadiusLG: radius.lg,
  borderRadiusOuter: radius.xs,
  controlHeightSM: size.controlSM,
  controlHeight: size.control,
  controlHeightLG: size.controlLG,

  /* Elevation */
  boxShadow: shadow.lg,
  boxShadowSecondary: shadow.md,
  boxShadowTertiary: shadow.xs,
  boxShadowCard: shadow.md,

  /* Motion */
  motionDurationFast: motion.fast,
  motionDurationMid: motion.mid,
  motionDurationSlow: motion.slow,
  motionEaseOut: motion.easeOut,

  wireframe: false,
};

export const themeConfig = {
  algorithm: theme.defaultAlgorithm,
  // Emits CSS variables (--ant-color-primary, …) usable in your own CSS.
  // Requires antd >= 5.12. Set hashed: true again if you ever run two antd copies.
  cssVar: true,
  hashed: false,
  token,
  components,
};

// Props spread onto <ConfigProvider>. Keep this for things tokens can't express —
// never for sizes/colors, which belong in tokens.
export const componentConfig = {
  card: {
    style: { boxShadow: shadow.xs },
  },
  modal: {
    styles: {
      // Brand signature: accent bar on dialog titles.
      header: {
        borderInlineStart: `3px solid ${brand[500]}`,
        paddingInlineStart: 12,
      },
    },
  },
};

export * from './foundations';
